import React, { useState, useEffect } from 'react';
import { X, UploadCloud, CheckCircle, Mic, MicOff } from 'lucide-react';
import useVoiceRecognition from '../hooks/useVoiceRecognition';
import useMobileModalMode from '../hooks/useMobileModalMode';
import { parseShipmentVoice } from '../utils/voiceHelper';
import api from '../utils/api';
import { getLocalDateInputValue, toDateInputValue } from '../utils/dateUtils';
import toast from 'react-hot-toast';

const AddShipmentModal = ({ isOpen, onClose, onRefresh, editData = null, sellers = [] }) => {
    const [formData, setFormData] = useState({
        seller: '',
        buyer: '',
        route: 'Ambar',
        payer: 'Alıcı',
        city: '',
        sackCount: '',
        createdAt: getLocalDateInputValue()
    });

    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { isListening, supported, text, startListening, stopListening, setText } = useVoiceRecognition();

    useMobileModalMode(isOpen);

    useEffect(() => {
        if (text && !isListening) {
            const parsed = parseShipmentVoice(text, sellers);

            setFormData(prev => ({
                ...prev,
                sackCount: parsed.sackCount || prev.sackCount,
                route: parsed.route || prev.route,
                payer: parsed.payer || prev.payer,
                city: parsed.city || prev.city,
                seller: parsed.seller || prev.seller,
            }));

            toast.success('Ses algılandı ve kargo formuna aktarıldı.');
            setText('');
        }
    }, [text, isListening, sellers, setText]);

    useEffect(() => {
        if (editData && isOpen) {
            setFormData({
                seller: editData.seller || '',
                buyer: editData.buyer || '',
                route: editData.route || 'Ambar',
                payer: editData.payer || 'Alıcı',
                city: editData.city || '',
                sackCount: editData.sackCount || '',
                createdAt: toDateInputValue(editData.createdAt)
            });
            setImagePreview(editData.imageUrl ? (process.env.NODE_ENV === 'production' ? editData.imageUrl : `http://localhost:5000${editData.imageUrl}`) : null);
            setImageFile(null);
        } else if (!editData && isOpen) {
            setFormData({
                seller: '', buyer: '', route: 'Ambar', payer: 'Alıcı', city: '', sackCount: '', createdAt: getLocalDateInputValue()
            });
            setImagePreview(null);
            setImageFile(null);
        }
    }, [editData, isOpen]);

    if (!isOpen) return null;

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            let uploadedImageUrl = editData ? editData.imageUrl : null;

            // Eğer yeni bir resim seçildiyse önce onu yükle
            if (imageFile) {
                const imgFormData = new FormData();
                imgFormData.append('receiptImage', imageFile);

                const uploadRes = await api.post('/shipments/upload', imgFormData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                uploadedImageUrl = uploadRes.data.imageUrl;
            }

            const payload = {
                ...formData,
                sackCount: parseInt(formData.sackCount, 10),
                imageUrl: uploadedImageUrl
            };

            if (editData && editData.id) {
                await api.put(`/shipments/${editData.id}`, payload);
            } else {
                await api.post('/shipments', payload);
            }

            onRefresh({ resetToFirstPage: !editData });
            onClose();
        } catch (error) {
            console.error('Kargo Kaydı Başarısız:', error);
            toast.error('İşlem başarısız. Lütfen yetkilerinizi kontrol edin.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-slate-900/80 backdrop-blur-sm transition-all duration-300 sm:items-center sm:p-4">
            <div className="bg-slate-800 border border-slate-700 w-full max-w-2xl rounded-t-2xl shadow-2xl flex flex-col max-h-[calc(100dvh-0.5rem)] sm:max-h-[90vh] sm:rounded-2xl animate-slide-up">

                {/* Modal Header */}
                <div className="flex justify-between items-center p-5 sm:p-6 border-b border-slate-700/50">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <h2 className="text-xl font-bold text-white flex items-center gap-2">
                            {editData ? "Kargo Güncelle" : "Yeni Sevkiyat Ekle"}
                        </h2>
                        {supported && !editData && (
                            <button
                                type="button"
                                onClick={isListening ? stopListening : startListening}
                                className={`flex flex-1 sm:flex-none justify-center items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${isListening
                                    ? 'bg-red-500/20 text-red-400 animate-pulse border border-red-500/50 shadow-lg shadow-red-500/20'
                                    : 'bg-primary-500/20 text-primary-400 hover:bg-primary-500/30 border border-primary-500/30'
                                    }`}
                                title="Sesle Doldur"
                            >
                                {isListening ? <MicOff size={16} /> : <Mic size={16} />}
                                {isListening ? 'Dinleniyor...' : 'Sesle Doldur (Akıllı)'}
                            </button>
                        )}
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-xl transition-colors shrink-0">
                        <X size={20} />
                    </button>
                </div>

                {/* Modal Body */}
                <div className="p-5 sm:p-6 overflow-y-auto custom-scrollbar">
                    <form id="shipmentForm" onSubmit={handleSubmit} className="space-y-6">

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Satıcı Seçimi */}
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Satıcı</label>
                                <select
                                    className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                                    value={formData.seller}
                                    onChange={(e) => setFormData({ ...formData, seller: e.target.value })}
                                    required
                                >
                                    <option value="">Satıcı Seçin...</option>
                                    {sellers.map((s) => (
                                        <option key={s.id} value={s.name}>{s.name}</option>
                                    ))}
                                </select>
                                {sellers.length === 0 && <p className="text-xs text-orange-400 mt-1">Önce Yönetici satıcı listesine ekleme yapmalıdır.</p>}
                            </div>

                            {/* Alıcı */}
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Alıcı (Kime)</label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500/50 placeholder-slate-600"
                                    placeholder="Alıcı Adı veya Firma"
                                    value={formData.buyer}
                                    onChange={(e) => setFormData({ ...formData, buyer: e.target.value })}
                                    required
                                />
                            </div>

                            {/* Şehir */}
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Gönderilen Şehir</label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500/50 placeholder-slate-600"
                                    placeholder="İl - İlçe"
                                    value={formData.city}
                                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                    required
                                />
                            </div>

                            {/* Çuval Sayısı */}
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Çuval Sayısı</label>
                                <input
                                    type="number"
                                    min="1"
                                    className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500/50 placeholder-slate-600"
                                    placeholder="Örn: 5"
                                    value={formData.sackCount}
                                    onChange={(e) => setFormData({ ...formData, sackCount: e.target.value })}
                                    required
                                />
                            </div>

                            {/* Gönderim Yolu (Datalist destekli serbest metin) */}
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Gönderim Yolu (Kargo/Firma)</label>
                                <input
                                    type="text"
                                    list="route-list"
                                    className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500/50 placeholder-slate-600"
                                    placeholder="Örn: Yurtiçi Kargo, Fedex, Özel Araç..."
                                    value={formData.route}
                                    onChange={(e) => setFormData({ ...formData, route: e.target.value })}
                                    required
                                />
                                <datalist id="route-list">
                                    <option value="Ambar" />
                                    <option value="Yurtiçi Kargo" />
                                    <option value="Sürat Kargo" />
                                    <option value="MNG Kargo" />
                                    <option value="Aras Kargo" />
                                    <option value="PTT Kargo" />
                                    <option value="DHL / Yurtdışı" />
                                    <option value="FedEx" />
                                    <option value="UPS Kargo" />
                                    <option value="Özel Araç" />
                                </datalist>
                            </div>

                            {/* Ödeme Kime Ait */}
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Ödeme (Kime Ait?)</label>
                                <select
                                    className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                                    value={formData.payer}
                                    onChange={(e) => setFormData({ ...formData, payer: e.target.value })}
                                    required
                                >
                                    <option value="Alıcı">Alıcı Ödemeli</option>
                                    <option value="Biz (Gönderici)">Biz Ödüyoruz (Gönderici)</option>
                                </select>
                            </div>

                        </div>

                        {/* Kayıt Tarihi */}
                        <div className="mt-6">
                            <label className="block text-sm font-medium text-slate-300 mb-2">Kayıt Tarihi</label>
                            <input
                                type="date"
                                className="w-full md:w-1/2 px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500/50 cursor-pointer"
                                value={formData.createdAt}
                                onChange={(e) => setFormData({ ...formData, createdAt: e.target.value })}
                            />
                            <p className="text-xs text-slate-500 mt-1">Varsayılan: Bugünün tarihi. Gerekirse değiştirin.</p>
                        </div>

                        {/* Kargo Fotoğrafı (Çuval / Fiş) Yükleme */}
                        <div className="mt-8 border-t border-slate-700/50 pt-6">
                            <h3 className="text-sm font-semibold text-slate-300 mb-4 flex items-center gap-2">
                                <UploadCloud size={18} className="text-secondary-400" />
                                Kargo Fotoğrafı (Çuval veya Gönderim Fişi)
                            </h3>

                            <div className="flex flex-col md:flex-row gap-6 items-start">
                                {/* Resim Yükleme Alanı */}
                                <div className="flex-1 w-full">
                                    <label className="relative flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-slate-600 rounded-2xl cursor-pointer bg-slate-800 hover:bg-slate-700/50 transition-colors group">
                                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                            <UploadCloud size={32} className="text-slate-400 mb-3 group-hover:text-primary-400 group-hover:scale-110 transition-all" />
                                            <p className="mb-2 text-sm text-slate-400 group-hover:text-slate-300">
                                                <span className="font-semibold text-primary-400">Resim Seç</span> veya Galeriyi Aç
                                            </p>
                                            <p className="text-xs text-slate-500 text-center px-4">JPEG, PNG, JPG (Kameradan çekmek için telefondan tıklayın)</p>
                                        </div>
                                        <input type="file" className="hidden" accept="image/*" onChange={handleImageChange} />
                                    </label>
                                </div>

                                {/* Resim Önizleme */}
                                {imagePreview && (
                                    <div className="w-full md:w-48 h-40 relative rounded-2xl overflow-hidden border border-slate-700 shadow-inner group">
                                        <img src={imagePreview} alt="Kargo Önizleme" className="w-full h-full object-cover" />
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all">
                                            <CheckCircle size={32} className="text-green-400" />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                    </form>
                </div>

                {/* Modal Footer */}
                <div className="sticky bottom-0 z-10 p-4 sm:p-6 border-t border-slate-700/50 flex justify-end gap-3 bg-slate-900/95 shrink-0 pb-safe-offset">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-2.5 rounded-xl font-medium text-slate-300 hover:bg-slate-800 transition-colors"
                    >
                        İptal
                    </button>
                    <button
                        type="submit"
                        form="shipmentForm"
                        disabled={isSubmitting}
                        className={`px-8 py-2.5 rounded-xl font-bold tracking-wide transition-all shadow-lg ${isSubmitting
                            ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                            : 'bg-primary-600 hover:bg-primary-500 text-white shadow-primary-500/25 hover:scale-105 active:scale-95'
                            }`}
                    >
                        {isSubmitting ? 'Kaydediliyor...' : (editData ? 'Güncelle' : 'Kaydet')}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default AddShipmentModal;
